/**
 * Shared dialog design tokens.
 *
 * Single home for the spacing + sizing values every dialog adopts via
 * `BaseDialog` / `ConfirmDialog`. The paper-level look (radius + shadow)
 * lives as a global `MuiDialog` override in `main.tsx`; header / body /
 * footer spacing lives HERE and is applied inside `BaseDialog` (never as
 * a global `MuiDialogTitle/Content/Actions` override, which would restyle
 * the ~20 dialogs not yet migrated).
 *
 * Values mirror the restyled `ExceptionDrawer` header (the visual north
 * star) and `AppDrawerHeader` so the dialog + drawer languages match.
 * Follows the plain const-export pattern of the sibling `theme/` files
 * (`colors.ts`, `exceptionTableStyles.ts`, `textFieldStyles.ts`).
 */
import type { DialogProps } from '@mui/material'

// Header (DialogTitle region) - matches AppDrawerHeader px:2 py:1.25
export const DIALOG_HEADER_PX = 2
export const DIALOG_HEADER_PY = 1.25

// Body (DialogContent region)
export const DIALOG_BODY_PX = 2
export const DIALOG_BODY_PY = 1.5

// Footer (DialogActions region) - right-aligned, gap 1
export const DIALOG_FOOTER_PX = 2
export const DIALOG_FOOTER_PY = 1.25
export const DIALOG_FOOTER_GAP = 1

// Paper-level look (also enforced globally in main.tsx for unmigrated
// dialogs; re-exported here so BaseDialog can reference the same numbers).
export const DIALOG_PAPER_RADIUS = 8

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl' | 'fullscreen'

/**
 * Maps a `BaseDialog` size preset to the MUI `Dialog` `maxWidth` prop.
 * `fullscreen` is handled separately via `fullScreen` and returns false
 * so `maxWidth` is not applied.
 */
export const dialogMaxWidth = (
  size: DialogSize,
): DialogProps['maxWidth'] => {
  switch (size) {
    case 'sm':
      return 'sm'
    case 'md':
      return 'md'
    case 'lg':
      return 'lg'
    case 'xl':
      return 'xl'
    case 'fullscreen':
      return false
  }
}
